import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading, success, failed
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus("failed");
        return;
      }

      try {
        const response = await fetch(`${API}/payments/status/${sessionId}`, {
          credentials: "include"
        });

        if (!response.ok) {
          throw new Error("Failed to check payment status");
        }

        const data = await response.json();
        setPaymentData(data);

        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        } else if (data.status === "expired") {
          setStatus("failed");
          return;
        }

        // If payment is still pending, continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    };

    pollPaymentStatus();
  }, [sessionId]);

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format((cents || 0) / 100);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center p-6">
      <Card className="bg-[#121214] border-[#27272A] max-w-md w-full" data-testid="payment-result">
        <CardContent className="pt-8 pb-8 text-center">
          {status === "loading" && (
            <>
              <Loader2 size={64} className="text-[#D4AF37] mx-auto mb-6 animate-spin" />
              <h2 className="text-xl font-semibold text-[#FAFAFA] mb-2">Processing Payment</h2>
              <p className="text-[#A1A1AA]">Please wait while we confirm your payment...</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 rounded-full bg-[#22C55E]/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 size={48} className="text-[#22C55E]" />
              </div>
              <h2 className="text-2xl font-bold text-[#FAFAFA] mb-2">Payment Successful!</h2>
              <p className="text-[#A1A1AA] mb-6">Thank you for your payment.</p>
              {paymentData && (
                <div className="bg-[#1A1A1D] rounded-lg p-4 mb-6">
                  <p className="text-sm text-[#71717A]">Amount Paid</p>
                  <p className="text-2xl font-bold text-[#D4AF37]">
                    {formatCurrency(paymentData.amount_total)}
                  </p>
                </div>
              )}
              <Button 
                onClick={() => navigate("/client/invoices")} 
                className="btn-gold"
                data-testid="back-to-invoices-btn"
              >
                Back to Invoices
              </Button>
            </>
          )}

          {status === "failed" && (
            <>
              <div className="w-20 h-20 rounded-full bg-[#EF4444]/20 flex items-center justify-center mx-auto mb-6">
                <XCircle size={48} className="text-[#EF4444]" />
              </div>
              <h2 className="text-2xl font-bold text-[#FAFAFA] mb-2">Payment Issue</h2>
              <p className="text-[#A1A1AA] mb-6">
                We couldn&apos;t confirm your payment. Please check your email for confirmation or contact support.
              </p>
              <div className="flex gap-3 justify-center">
                <Button 
                  onClick={() => navigate("/client/invoices")} 
                  variant="outline"
                  className="border-[#27272A] text-[#A1A1AA]"
                >
                  Back to Invoices
                </Button>
                <Button 
                  onClick={() => window.location.reload()} 
                  className="btn-gold"
                >
                  Try Again
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
